import { useEffect, useRef, useState } from "react";
import { useScroll } from "framer-motion";
import LaptopCanvas from "./LaptopCanvas";
import HeroOverlay from "./HeroOverlay";
import WalshOS from "./WalshOS";
import MobileExperience from "./MobileExperience";
import { useDrivenStyle, mapStops } from "./timeline";

const BG_STOPS = [0, 0.4, 0.5, 0.93, 0.975, 1];
const BG_COLORS = [
  [247, 245, 240],
  [247, 245, 240],
  [11, 13, 15],
  [11, 13, 15],
  [247, 245, 240],
  [247, 245, 240],
];

function bgAt(p) {
  // mapStops interpolates colour indices; blend the two nearest colours
  const c = mapStops(p, BG_STOPS, BG_COLORS.map((_, i) => i));
  const lo = Math.floor(c);
  const hi = Math.min(BG_COLORS.length - 1, lo + 1);
  const t = c - lo;
  const a = BG_COLORS[lo];
  const b = BG_COLORS[hi];
  const mix = a.map((v, k) => Math.round(v + (b[k] - v) * t));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}

/**
 * CinematicSection - ONE pinned scene. ONE laptop. ONE continuous story.
 * A 1000vh scroll track with a sticky viewport; progress drives the WebGL
 * laptop, the hero overlay and the DOM "Walsh OS" desktop as a single timeline.
 */

function useIsMobile() {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1023px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

export default function CinematicSection() {
  const isMobile = useIsMobile();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const bgRef = useDrivenStyle(scrollYProgress, (el, p) => {
    el.style.backgroundColor = bgAt(p);
  });

  if (isMobile) return <MobileExperience />;

  return (
    <section
      ref={sectionRef}
      data-testid="cinematic-section"
      className="relative h-[1000vh]"
    >
      {/* Anchor: jumping to #work lands inside the pinned scene at the portfolio */}
      <span id="work" className="absolute left-0 w-px h-px" style={{ top: "64%" }} />

      <div className="sticky top-0 h-[100svh] overflow-hidden">
        <div ref={bgRef} className="absolute inset-0" style={{ backgroundColor: "#f7f5f0" }} />
        <LaptopCanvas progress={scrollYProgress} mode="desktop" />
        <HeroOverlay progress={scrollYProgress} />
        <WalshOS progress={scrollYProgress} />
      </div>
    </section>
  );
}
